import { Close } from "@mui/icons-material";
import { Box, Dialog, Typography } from "@mui/material";
import styles from "./Accounts.module.scss";
import { useFormik } from "formik";
import {
  CommonButton,
  CommonOutlinedButton,
  CustomRequiredInputLabel,
  CustomTextFeild,
} from "../../common/StyledComponents/StyledFields";
import ErrorPopup from "../../common/ErrorPopup";
import {
  apiRequest,
  catchErrorMessageHandler,
} from "../../../infrastructure/backendService";
import toast from "react-hot-toast";
import apiPath from "../../../config/apiPaths.json";
import { useEffect, useState } from "react";
import CustomSelectField from "../../common/CustomSelectField/CustomSelectField";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import { object, string } from "yup";
import SuccessPopup from "../../devotee/donation/successPopup";

export const groupingStyles = {
  fieldContainer: {
    maxWidth: { xs: "100%", sm: "320px" },
    width: { xs: "100%", sm: "320px" },
    pr: { xs: "0", sm: "15px" },
    mb: "10px",
  },
  groupedContainer: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
  },
};

const CreateorEditAccountDialog = (props: any) => {
  const {
    open,
    handleClose,
    isUpdateAccount,
    handleCloseGetAccounts,
    accountDetails,
  } = props;

  const [categoriesList, setCategoriesList] = useState<any[]>([]);
  const [isBtnLoading, setIsBtnLoading] = useState(false);

  const initialValues = {
    accountId: "",
    category: "",
    description: "",
  };

  const validationSchema = object().shape({
    accountId: string()
      .trim()
      .required("Account ID is required")
      .length(7, "Account ID must be exactly 7 characters")
      .matches(
        /^[A-Z]{2}0{5}$/,
        "Account ID must start with two capital letters followed by five zeros"
      ),
    category: string().trim().required("Please select the category"),
    description: string().trim().required("Description is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {
      setIsBtnLoading(true);
      if (isUpdateAccount) {
        handleUpdateAccount(values);
      } else {
        handleCreateAccount(values);
      }
    },
    enableReinitialize: true,
  });

  const handleCreateAccount = (values: any) => {
    const payload = {
      ...values,
      mainAccount: true,
    };
    apiRequest({
      method: "POST",
      endpoint: apiPath.createAccount,
      includeToken: true,
      body: payload,
    })
      .then((res) => {
        setIsBtnLoading(false);
        toast.custom(
          <SuccessPopup
            headerMessage="Create New Account"
            showMessage={`Successfully created the account`}
          />
        );
        formik.resetForm();
        handleCloseGetAccounts();
      })
      .catch((err) => {
        setIsBtnLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const handleUpdateAccount = (values: any) => {
    apiRequest({
      method: "POST",
      endpoint: apiPath.updateAccount?.replace(
        "{accountsId}",
        values?.accountId
      ),
      includeToken: true,
      params: {
        category: values?.category,
      },
    })
      .then((res) => {
        setIsBtnLoading(false);
        toast.custom(
          <SuccessPopup
            headerMessage="Update Account"
            showMessage={`Successfully updated the account`}
          />
        );
        formik.resetForm();
        handleCloseGetAccounts();
      })
      .catch((err) => {
        setIsBtnLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const getCategories = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.getAllCategories,
      includeToken: true,
      params: {
        page: 0,
        size: 300,
        direction: "asc",
        sortBy: "name",
      },
    })
      .then((res) => {
        setCategoriesList(res?.data?.content);
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  useEffect(() => {
    getCategories();
    if (isUpdateAccount) {
      formik.setFieldValue("accountId", accountDetails?.accountId);
      formik.setFieldValue("description", accountDetails?.description);
      formik.setFieldValue("category", accountDetails?.category);
    }
  }, []);

  const handleFieldChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };

  const renderFieldsComponent = () => {
    return (
      <>
        <Box sx={groupingStyles.groupedContainer}>
          <Box sx={groupingStyles.fieldContainer}>
            {CustomRequiredInputLabel("Account ID", true)}
            <CustomTextFeild
              fullWidth
              required
              value={formik.values.accountId}
              className={styles.textField}
              onChange={(e) =>
                handleFieldChange("accountId", e.target.value?.toUpperCase())
              }
              disabled={isUpdateAccount}
              name="accountId"
              placeholder="Account ID"
              InputProps={{
                inputProps: {
                  maxLength: 7,
                  readOnly: isUpdateAccount,
                },
              }}
            />
            {formik.touched.accountId && formik?.errors?.accountId && (
              <Typography sx={{ color: "red", fontSize: "12px" }}>
                {formik?.errors?.accountId}
              </Typography>
            )}
          </Box>
          <Box sx={groupingStyles.fieldContainer}>
            {CustomRequiredInputLabel("Category", true)}
            <CustomSelectField
              handleDropdownChange={handleFieldChange}
              errorMessage={
                formik.touched.category ? formik.errors.category : ""
              }
              dropDownList={categoriesList}
              value={formik.values.category}
              fieldName="category"
              placeholder="Select category"
              optionLabel="name"
              optionValue="id"
            />
          </Box>
        </Box>
        <Box
          sx={{
            ...groupingStyles.fieldContainer,
            width: { xs: "100%", sm: "calc(100% - 15px)" },
            maxWidth: "100%",
          }}
        >
          {CustomRequiredInputLabel("Description", true)}
          <textarea
            className={styles.text_area_field}
            value={formik.values.description}
            onChange={formik.handleChange}
            name="description"
            maxLength={200}
            readOnly={isUpdateAccount}
            disabled={isUpdateAccount}
          />
          {formik.touched.description && formik?.errors?.description ? (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {formik?.errors?.description}
            </Typography>
          ) : (
            ""
          )}
        </Box>
      </>
    );
  };

  const renderButtonsComponent = () => {
    return (
      <Box className={styles.buttonsContainer}>
        <CommonButton
          onClick={() => {
            formik.handleSubmit();
          }}
          sx={{ mr: "10px" }}
        >
          <ButtonLoader
            isBtnLoading={isBtnLoading}
            btnText={isUpdateAccount ? "Update" : "Submit"}
          />
        </CommonButton>
        <CommonOutlinedButton
          onClick={() => {
            !isBtnLoading && handleClose();
          }}
        >
          Cancel
        </CommonOutlinedButton>
      </Box>
    );
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "710px" },
          maxWidth: { xs: "100%", sm: "710px" },
          margin: { xs: "10px", sm: "20px" },
        },
      }}
    >
      <Box
        className={styles.dialog_main_container}
        sx={{
          width: "100%",
        }}
      >
        <Box className={styles.dialog_title_container}>
          <Typography className={styles.title}>
            {isUpdateAccount ? "Update Account" : "Create New Account"}
          </Typography>
          <Close
            sx={{ color: "white", cursor: "pointer" }}
            onClick={handleClose}
          />
        </Box>
        <Box className={styles.dialog_inner_container}>
          {renderFieldsComponent()}
          {renderButtonsComponent()}
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateorEditAccountDialog;
